import React from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { PaymentTermsSection } from '@/components/config_forms/invoice/PaymentTermsSection';
import { LateFeeSection } from '@/components/config_forms/invoice/LateFeeSection';
import { EarlyPaymentSection } from '@/components/config_forms/invoice/EarlyPaymentSection';
import { CancellationPolicySection } from '@/components/config_forms/invoice/CancellationPolicySection';
import { RushPremiumPolicySection } from '@/components/config_forms/invoice/RushPremiumPolicySection';
import { RemindersSection } from '@/components/config_forms/invoice/RemindersSection';
import { EmploymentTypeSection } from '@/components/config_forms/invoice/EmploymentTypeSection';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { fieldLabelClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { RushPremiumTier } from '@/types';

type CancellationRule = {
  days: number | null;
  type: "percentage" | "fixed";
  value: number | null;
};

interface InvoiceConfigFormProps {
  // Data
  title: string;
  description: string;
  
  // Accounts Payable Email (optional for client forms)
  showAccountsPayableEmail?: boolean;
  accountsPayableEmailProps?: Record<string, unknown>;
  accountsPayableEmailError?: React.ReactNode;
  
  // Payment Terms
  paymentTermsDays: number | null;
  paymentTermsProps?: Record<string, unknown>;
  paymentTermsError?: React.ReactNode;
  
  // Late Fee
  useLateFee: boolean;
  lateFeeType: 'percentage' | 'fixed';
  recurringLateFee: string;
  onToggleLateFee: (checked: boolean) => void;
  onLateFeeTypeChange: (type: 'percentage' | 'fixed') => void;
  onRecurringLateFeeChange: (value: string) => void;
  onIsRecurringLateFeeChange: (checked: boolean) => void;
  lateFeePctProps?: Record<string, unknown>;
  lateFeeFixedProps?: Record<string, unknown>;
  recurringIntervalProps?: Record<string, unknown>;
  lateFeePctError?: React.ReactNode;
  lateFeeFixedError?: React.ReactNode;
  recurringIntervalError?: React.ReactNode;
  
  // Early Payment
  useEarlyPaymentDiscount: boolean;
  earlyPaymentType: 'percentage' | 'fixed';
  onToggleEarlyPayment: (checked: boolean) => void;
  onEarlyPaymentTypeChange: (type: 'percentage' | 'fixed') => void;
  earlyDiscountPctProps?: Record<string, unknown>;
  earlyDiscountFixedProps?: Record<string, unknown>;
  earlyDaysProps?: Record<string, unknown>;
  earlyDiscountPctError?: React.ReactNode;
  earlyDiscountFixedError?: React.ReactNode;
  earlyDaysError?: React.ReactNode;
  
  // Cancellation Policy
  useCancellationPolicy: boolean;
  cancellationPolicy: CancellationRule[];
  onToggleCancellationPolicy: (checked: boolean) => void;
  onCancellationPolicyChange: (newRules: CancellationRule[]) => void;
  onDeactivateCancellationPolicy?: () => void;

  // Rush Premium Policy
  rushPremiumPolicy?: RushPremiumTier[];
  onRushPremiumPolicyChange?: (newRules: RushPremiumTier[]) => void;
  
  // Reminders
  autoSendReminders: boolean;
  reminderDays: number[];
  validationKey: string;
  onToggleAutoSendReminders: (checked: boolean) => void;
  onUpdateReminderDay: (idx: number, newValue: number | null) => void;
  onValidationComplete: (idx: number, isValid: boolean, validatedValue: number | null, originalValue: number) => void;
  onRemoveReminderDay: (index: number) => void;
  onAddEmptyReminder: () => void;
  onClearGroupError: (key: string) => void;
  
  // Employment Type
  employmentType: string;
  
  // Optional sections (for client forms)
  showEmploymentType?: boolean;
  isCustomized?: boolean;
  onRevert?: () => void;
  context?: 'user' | 'client';
}

const InvoiceConfigFormComponent: React.FC<InvoiceConfigFormProps> = ({
  title,
  description,
  showAccountsPayableEmail = false,
  accountsPayableEmailProps,
  accountsPayableEmailError,
  paymentTermsDays,
  paymentTermsProps,
  paymentTermsError,
  useLateFee,
  lateFeeType,
  recurringLateFee,
  onToggleLateFee,
  onLateFeeTypeChange,
  onRecurringLateFeeChange,
  onIsRecurringLateFeeChange,
  lateFeePctProps,
  lateFeeFixedProps,
  recurringIntervalProps,
  lateFeePctError,
  lateFeeFixedError,
  recurringIntervalError,
  useEarlyPaymentDiscount,
  earlyPaymentType,
  onToggleEarlyPayment,
  onEarlyPaymentTypeChange,
  earlyDiscountPctProps,
  earlyDiscountFixedProps,
  earlyDaysProps,
  earlyDiscountPctError,
  earlyDiscountFixedError,
  earlyDaysError,
  useCancellationPolicy,
  cancellationPolicy,
  onToggleCancellationPolicy,
  onCancellationPolicyChange,
  onDeactivateCancellationPolicy,
  rushPremiumPolicy,
  onRushPremiumPolicyChange,
  autoSendReminders,
  reminderDays,
  validationKey,
  onToggleAutoSendReminders,
  onUpdateReminderDay,
  onValidationComplete,
  onRemoveReminderDay,
  onAddEmptyReminder,
  onClearGroupError,
  employmentType,
  showEmploymentType = true,
  isCustomized,
  onRevert,
  context = 'user',
}) => {
  const shouldShowEarlyPaymentDaysInput = useEarlyPaymentDiscount;

  return (
    <Card
      className={cn(
        "animate-fade-in shadow-sm rounded-xl p-4 md:p-6 md:max-w-3xl md:mx-auto"
      )}
    >
      <div className="space-y-6">
        <div className="space-y-1 text-center">
          <div className="flex items-center justify-center gap-3">
            <h3 className="text-lg font-medium text-foreground">{title}</h3>
            {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        
        {showAccountsPayableEmail && (
          <div className="space-y-2">
            <Label htmlFor="accountsPayableEmail" className={fieldLabelClass}>Accounts Payable Email</Label>
            <Input
              id="accountsPayableEmail"
              type="email"
              placeholder="Optional email for sending invoices"
              {...(accountsPayableEmailProps || {})}
            />
            {accountsPayableEmailError}
          </div>
        )}
        
        <PaymentTermsSection 
          paymentTermsInput={{ inputProps: paymentTermsProps || {}, errorElement: paymentTermsError }} 
        />
        
        <div className="space-y-4">
          <LateFeeSection
            localUseLateFee={useLateFee}
            localLateFeeType={lateFeeType}
            recurringLateFee={recurringLateFee}
            lateFeePctProps={lateFeePctProps}
            lateFeePctError={lateFeePctError}
            lateFeeFixedProps={lateFeeFixedProps}
            lateFeeFixedError={lateFeeFixedError}
            recurringIntervalProps={recurringIntervalProps}
            recurringIntervalError={recurringIntervalError}
            onToggleLateFee={onToggleLateFee}
            onLateFeeTypeChange={onLateFeeTypeChange}
            onRecurringLateFeeChange={onRecurringLateFeeChange}
            onIsRecurringLateFeeChange={onIsRecurringLateFeeChange}
          />
          
          <EarlyPaymentSection
            localUseEarlyPaymentDiscount={useEarlyPaymentDiscount}
            localEarlyPaymentType={earlyPaymentType}
            shouldShowEarlyPaymentDaysInput={shouldShowEarlyPaymentDaysInput}
            earlyDiscountPctProps={earlyDiscountPctProps}
            earlyDiscountPctError={earlyDiscountPctError}
            earlyDiscountFixedProps={earlyDiscountFixedProps}
            earlyDiscountFixedError={earlyDiscountFixedError}
            earlyDaysProps={earlyDaysProps}
            earlyDaysError={earlyDaysError}
            onToggleEarlyPayment={onToggleEarlyPayment}
            onEarlyPaymentTypeChange={onEarlyPaymentTypeChange}
          />

          <CancellationPolicySection
            localUseCancellationPolicy={useCancellationPolicy}
            cancellationPolicy={cancellationPolicy}
            paymentTermsDays={paymentTermsDays}
            onToggleCancellationPolicy={onToggleCancellationPolicy}
            onCancellationPolicyChange={onCancellationPolicyChange}
            onDeactivate={onDeactivateCancellationPolicy}
          />

          {onRushPremiumPolicyChange && (
            <RushPremiumPolicySection
              rushPremiumPolicy={rushPremiumPolicy || []}
              onRushPremiumPolicyChange={onRushPremiumPolicyChange}
            />
          )}
        </div>
        
        <RemindersSection
          localAutoSendReminders={autoSendReminders}
          reminderDays={reminderDays}
          paymentTermsDays={paymentTermsDays}
          validationKey={validationKey}
          onToggleAutoSendReminders={onToggleAutoSendReminders}
          onUpdateReminderDay={onUpdateReminderDay}
          onValidationComplete={onValidationComplete}
          onRemoveReminderDay={onRemoveReminderDay}
          onAddEmptyReminder={onAddEmptyReminder}
          onClearGroupError={onClearGroupError}
        />
        
        {showEmploymentType && (
          <EmploymentTypeSection employmentType={employmentType} />
        )}
        {isCustomized && onRevert && (
          <div className="pt-4 border-t flex justify-center">
            <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
              Revert to Profile Defaults
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export const InvoiceConfigForm = React.memo(InvoiceConfigFormComponent);
InvoiceConfigForm.displayName = 'InvoiceConfigForm';
